import React from 'react';
import { AlertTriangle } from 'lucide-react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error("Uncaught error:", error, errorInfo);
        this.setState({ error, errorInfo });

        // Best-effort report — never let a broken reporter block the UI
        try {
            const apiUrl = (import.meta.env.MODE === 'production') ? '/api' : (import.meta.env.VITE_API_URL || '/api');
            fetch(`${apiUrl}/client-errors`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    message: error?.message || String(error),
                    stack: error?.stack || null,
                    componentStack: errorInfo?.componentStack || null,
                    url: window.location.href,
                    userAgent: navigator.userAgent,
                }),
            }).catch(() => {});
        } catch {}
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 p-8 text-center">
                    <AlertTriangle size={64} className="text-red-500 mb-6" />
                    <h1 className="text-4xl font-black mb-4">Something went wrong.</h1>
                    <p className="text-gray-600 max-w-md mb-8">
                        An unexpected error has occurred. The error has been logged.
                        <br />
                        Please try refreshing the page.
                    </p>
                    <div className="flex gap-4">
                        <button
                            onClick={() => window.location.reload()}
                            className="btn btn--primary"
                        >
                            Reload Page
                        </button>
                        <button
                            onClick={() => window.location.href = '/'}
                            className="btn btn--secondary"
                        >
                            Go Home
                        </button>
                    </div>
                    {import.meta.env.DEV && this.state.error && (
                        <div className="mt-12 w-full max-w-4xl text-left bg-white p-6 border-2 border-black overflow-auto">
                            <h3 className="text-red-600 font-bold mb-2">Error Details (Dev Only):</h3>
                            <pre className="text-xs font-mono whitespace-pre-wrap">
                                {this.state.error.toString()}
                                {this.state.errorInfo && this.state.errorInfo.componentStack}
                            </pre>
                        </div>
                    )}
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
